//scopes

import { AppointmentQueryFilters, WaitListFilters } from "../../api-client";

const scopes = {
  appointments: "appointments", // appoinments used in calendar
  appointmentsByRx: "appointmentsByRx",
  appointmentsBySeries: "appointmentsBySeries",
  appointmentsByPatient: "appointmentsByPatient",
  continuousAppointmentsByPatient: "continuousAppointmentsByPatient",
  cancelledAppointmentsByPatient: "cancelledAppointmentsByPatient",
  cancellationHistoryByPatient: "cancellationHistoryByPatient",
  blockedSlotsByPatient: "blockedSlotsByPatient",
  patientUnavailabilityByPatient: "patientUnavailabilityByPatient",
  therapistListByPatient: "therapistListByPatient",
  inactiveAppointmentsCount: "inactiveAppointmentsCount",
  // TODO: Add serviceAppointmentsByPatient scope when endpoint is available
  serviceAppointmentsByPatient: "serviceAppointmentsByPatient",
  cancelledAppointments: "cancelledAppointments", // global list of cancelled appointments for a location
  unscheduledAppointments: "unscheduledAppointments", // global list of unscheduled appointments for a location
  filteredAppointmentsByPatient: "filteredAppointmentsByPatient", // appointments filtered by patient and other filters
  waitListByRxSeries: "waitListByRxSeries", // waitlist grouped by Rx and Series
  previewSwapAppointments: "previewSwapAppointments", // preview swap appointments for series
  timeSlotDisplay: "timeSlotDisplay", // display payload for a timeslot by id
};

export const appointmentKeys = {
  appointments: {
    scope: scopes.appointments,
    all: (locationId: string) => [scopes.appointments, { locationId }] as const,
    detail: (locationId: string, id: string) => [scopes.appointments, { locationId, id }] as const,
  },

  appointmentsByRx: {
    scope: scopes.appointmentsByRx,
    all: () => [scopes.appointmentsByRx] as const,
    detail: (rxNumber: string) => [scopes.appointmentsByRx, { rxNumber }] as const,
  },

  appointmentsBySeries: {
    scope: scopes.appointmentsBySeries,
    all: () => [scopes.appointmentsBySeries] as const,
    detail: (seriesId: string) => [scopes.appointmentsBySeries, { seriesId }] as const,
  },
  appointmentsByPatient: {
    scope: scopes.appointmentsByPatient,
    all: () => [scopes.appointmentsByPatient] as const,
    detail: (patientId: number, addressId?: string) =>
      [scopes.appointmentsByPatient, { patientId, addressId }] as const,
  },
  serviceAppointmentsByPatient: {
    scope: scopes.serviceAppointmentsByPatient,
    all: () => [scopes.serviceAppointmentsByPatient] as const,
    detail: (patientId: number) => [scopes.serviceAppointmentsByPatient, { patientId }] as const,
  },

  continuousAppointmentsByPatient: {
    scope: scopes.continuousAppointmentsByPatient,
    all: () => [scopes.continuousAppointmentsByPatient] as const,
    detail: (patientId: string, addressId?: string) =>
      [scopes.continuousAppointmentsByPatient, { patientId, addressId }] as const,
  },

  cancelledAppointmentsByPatient: {
    scope: scopes.cancelledAppointmentsByPatient,
    all: () => [scopes.cancelledAppointmentsByPatient] as const,
    detail: (patientId: number) => [scopes.cancelledAppointmentsByPatient, { patientId }] as const,
  },

  cancellationHistoryByPatient: {
    scope: scopes.cancellationHistoryByPatient,
    all: () => [scopes.cancellationHistoryByPatient] as const,
    detail: (patientId: number) => [scopes.cancellationHistoryByPatient, { patientId }] as const,
  },

  blockedSlotsByPatient: {
    scope: scopes.blockedSlotsByPatient,
    all: () => [scopes.blockedSlotsByPatient] as const,
    detail: (patientId: string) => [scopes.blockedSlotsByPatient, { patientId }] as const,
  },

  patientUnavailabilityByPatient: {
    scope: scopes.patientUnavailabilityByPatient,
    all: () => [scopes.patientUnavailabilityByPatient] as const,
    detail: (patientId: string) => [scopes.patientUnavailabilityByPatient, { patientId }] as const,
  },

  therapistListByPatient: {
    scope: scopes.therapistListByPatient,
    all: () => [scopes.therapistListByPatient] as const,
    detail: (patientId: string) => [scopes.therapistListByPatient, { patientId }] as const,
  },

  inactiveAppointmentsCount: {
    scope: scopes.inactiveAppointmentsCount,
    all: () => [scopes.inactiveAppointmentsCount] as const,
    detail: (patientId: string) => [scopes.inactiveAppointmentsCount, { patientId }] as const,
  },

  // cancelled appointments - the query contains a list of all cancelled appointments for a location
  cancelledAppointments: {
    scope: scopes.cancelledAppointments,
    all: () => [scopes.cancelledAppointments] as const,
    detail: (locationId: string) => [scopes.cancelledAppointments, { locationId }] as const,
  },
  unscheduledAppointments: {
    scope: scopes.unscheduledAppointments,
    all: (locationId: string) => [scopes.unscheduledAppointments, { locationId }] as const,
    detail: (locationId: string, rank: boolean) =>
      [scopes.unscheduledAppointments, { locationId, rank }] as const,
  },
  filteredAppointmentsByPatient: {
    scope: scopes.filteredAppointmentsByPatient,
    all: () => [scopes.filteredAppointmentsByPatient] as const,
    detail: (patientId: number, filters: AppointmentQueryFilters) =>
      [scopes.filteredAppointmentsByPatient, { patientId, filters }] as const,
  },

  waitListByRxSeries: {
    scope: scopes.waitListByRxSeries,
    all: () => [scopes.waitListByRxSeries] as const,
    detail: (date: string) => [scopes.waitListByRxSeries, { date }] as const,
  },
  previewSwapAppointments: {
    scope: scopes.previewSwapAppointments,
    all: () => [scopes.previewSwapAppointments] as const,
    detail: (seriesIds: string[]) => [scopes.previewSwapAppointments, { seriesIds }] as const,
  },

  timeSlotDisplay: {
    scope: scopes.timeSlotDisplay,
    all: () => [scopes.timeSlotDisplay] as const,
    detail: (timeSlotId: string) => [scopes.timeSlotDisplay, { timeSlotId }] as const,
  },
};
